import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatar: string;
  utilization: number;
  accuracy: number;
  mandates: number;
  badges: string[];
}

export const IndividualPerformance = () => {
  // Fetch real team members with their performance data
  const { data: teamMembers = [], isLoading } = useQuery({
    queryKey: ['team-performance'],
    queryFn: async () => {
      const { data: users, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, role');

      if (error) throw error;

      if (!users || users.length === 0) return [];

      // Get performance metrics for each user
      const membersWithStats = await Promise.all(users.map(async (user) => {
        // Count active mandates
        const { count: mandateCount } = await supabase
          .from('mandate_team_members')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // Calculate accuracy from validated transactions
        const { data: transactions } = await supabase
          .from('transactions')
          .select('ai_classification_confidence')
          .not('ai_classification_confidence', 'is', null);

        const accuracy = transactions && transactions.length > 0
          ? transactions.reduce((sum, t) => sum + (t.ai_classification_confidence || 0), 0) / transactions.length
          : 0;

        // Calculate utilization (simple: mandates / 10 * 100)
        const utilization = Math.min((mandateCount || 0) / 10 * 100, 100);

        // Determine badges based on performance
        const badges: string[] = [];
        if (accuracy >= 99) badges.push('award');
        if (utilization >= 90) badges.push('bolt');
        if (mandateCount && mandateCount >= 10) badges.push('rocket');

        return {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          role: user.role || 'Team Member',
          avatar: '', // No avatar for now
          utilization: Math.round(utilization),
          accuracy: Math.round(accuracy * 10) / 10,
          mandates: mandateCount || 0,
          badges
        };
      }));

      return membersWithStats;
    }
  });
  const getBadgeIcon = (badge: string) => {
    const icons: Record<string, string> = {
      award: "fa-solid fa-award",
      bolt: "fa-solid fa-bolt", 
      heart: "fa-solid fa-heart",
      rocket: "fa-solid fa-rocket"
    };
    return icons[badge] || "fa-solid fa-star";
  };

  const getBadgeTitle = (badge: string) => {
    const titles: Record<string, string> = {
      award: "Top Accuracy",
      bolt: "Fastest Processor",
      heart: "Client Favorite",
      rocket: "Most Improved"
    };
    return titles[badge] || "Achievement";
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 90) return "text-success";
    if (utilization >= 70) return "text-warning";
    return "text-brand-danger";
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border">
        <div className="text-center py-8 text-brand-text-secondary">Loading team performance...</div>
      </div>
    );
  }

  if (teamMembers.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border">
        <div className="text-center py-8 text-brand-text-secondary">No team members found</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Individual Performance</h3>
        <div className="flex items-center gap-2">
          <button className="h-7 px-2.5 py-1.5 bg-brand-primary rounded-lg text-center text-white text-xs">
            All
          </button>
          <button className="h-7 px-2.5 py-1.5 bg-brand-secondary rounded-lg text-center text-brand-primary text-xs">
            Bookkeepers
          </button>
          <button className="h-7 px-2.5 py-1.5 bg-brand-secondary rounded-lg text-center text-brand-primary text-xs">
            Specialists
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teamMembers.map((member) => (
          <div key={member.id} className="border border-brand-border rounded-xl p-4 flex flex-col items-center text-center">
            <div
              className={`w-16 h-16 rounded-full mb-3 bg-brand-primary text-white flex items-center justify-center text-xl font-bold ${member.badges.includes('award') ? 'border-2 border-brand-accent' : ''}`}
            >
              {member.name.split(' ').map(n => n[0]).join('')}
            </div>
            <h4 className="font-bold text-brand-text-primary">{member.name}</h4>
            <p className="text-xs text-brand-text-secondary">{member.role}</p>
            
            <div className="flex gap-2 my-3">
              {member.badges.map((badge, index) => (
                <span 
                  key={index}
                  className="p-1.5 bg-brand-secondary rounded-full" 
                  title={getBadgeTitle(badge)}
                >
                  <i className={`${getBadgeIcon(badge)} text-brand-primary`}></i>
                </span>
              ))}
            </div>
            
            <div className="w-full text-left text-xs space-y-2">
              <div className="flex justify-between">
                <span>Utilization:</span> 
                <span className={`font-medium ${getUtilizationColor(member.utilization)}`}>
                  {member.utilization}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Accuracy:</span> 
                <span className="font-medium">{member.accuracy}%</span>
              </div>
              <div className="flex justify-between">
                <span>Active Mandates:</span> 
                <span className="font-medium">{member.mandates}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};